import {Action, createSelector, Selector, State, StateContext, Store} from "@ngxs/store";
import {PRODUCTS_STATE} from "../index";
import {Injectable} from "@angular/core";
import {ProductsStateModel} from "./products.model";
import {Products} from "./products.action";
import {FilterSetting, Product, UpdateProductInput} from "../../shared/graphql/generated/graphql";
import {catchError, filter, first, map, mergeMap, of, switchMap, tap} from "rxjs";
import {NavigationEnd, Router} from "@angular/router";
import {MenuItem} from "../../layout/app.menu.items";
import {Storage} from "../storage/storage.actions";
import {StorageState} from "../storage/storage.state";
import {AuthState} from "../auth/auth.state";
import {getProductMenuItems} from "./product-menu-items";
import {ProductsService} from "src/app/shared/services/products.service";
import {MessageService, TreeNode} from "primeng/api";
import {SettingsState} from "../settings/settings.state";
import {
  AsyncInitializationService,
  AsyncInitializationTaskState
} from "../../shared/services/async-initialization.service";
import { Menus } from "../menus/menus.action";
import { AnalysisMode, BOTTOM_UP_PRODUCT_ID, DEFAULT_METRIC_ID, getMetricOptions, DEFAULT_ANALYSIS_MODE, MetricOption } from "./products.constants";
import { bottomUpMetric } from "src/app/dashboards/shared/analyses/bottom-up/_shared/bottom-up-metric";
import { Metric } from "../../dashboards/shared/dashboard-config/dashboard-config.model";
import { DashboardUtils } from "../../dashboards/DashboardUtils";

@State<ProductsStateModel>({
  name: PRODUCTS_STATE,
  defaults: {
    selectedProductId: undefined,
    selectedProduct: undefined,
    products: [],
    productsLoading: false,
    selectedProductMenuModel: [],
    analysisMode: DEFAULT_ANALYSIS_MODE,
    metrics: [],
    metricId: DEFAULT_METRIC_ID,
    fetching: false,
    settings: []
  }
})
@Injectable()
export class ProductsState {

  constructor(
    private router: Router,
    private store: Store,
    private productService: ProductsService,
    private messageService: MessageService,
    private asyncInitializationService: AsyncInitializationService
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (event.url.toString().includes('/dashboards/')) {
          const res = new RegExp('\/dashboards\/(\\d+)').exec(event.url.toString());
          if (res && res.length > 1) {
            const productIdToSelect = parseInt(res[1]);
            this.store.dispatch(new Products.SelectProductById(productIdToSelect));
          }
        }
      }
    });
  }

  @Selector()
  static selectedProduct(state: ProductsStateModel) {
    return state.selectedProduct;
  }

  @Selector()
  static selectedProductId(state: ProductsStateModel) {
    return state.selectedProductId;
  }

  @Selector()
  static selectedProductMenuModel(state: ProductsStateModel) {
    return state.selectedProductMenuModel;
  }

  @Selector()
  static metricOptions(state: ProductsStateModel) {
    return state.metrics
  }

  @Selector()
  static selectedMetric(state: ProductsStateModel) {
    return state.metrics.find(opt => opt.id == state.metricId)
  }

  @Selector()
  static selectedBottomUpMetric(state: ProductsStateModel) {
    return bottomUpMetric(state.metricId)
  }

  @Selector()
  static products(state: ProductsStateModel) {
    return state.products; //.sort((a, b) => a.id > b.id ? 1 : -1);
  }

  @Selector([ProductsState.products , AuthState.userClaims])
  static accessibleProducts(products: Product[], userClaims: string[]): Product[] {
    if (products == undefined) return [];

    return products.filter(product => {
      if (userClaims.includes('admin_access')) return true;
      if (!product.isEnabled) return false;
      return userClaims.includes(`access_product_${product.id}`);
    });
  }

  @Selector([ProductsState.accessibleProducts])
  static topDownProducts(accessibleProducts: Product[]): Product[] {
    return accessibleProducts.filter(product => product.id !== BOTTOM_UP_PRODUCT_ID);
  }

  /** The analysis mode can only be switched when both modes actually have a product to offer. */
  @Selector([ProductsState.accessibleProducts, ProductsState.topDownProducts])
  static isAnalysisModeSwitchAvailable(accessibleProducts: Product[], topDownProducts: Product[]): boolean {
    const hasBottomUpProduct = accessibleProducts.some(product => product.id === BOTTOM_UP_PRODUCT_ID);
    return hasBottomUpProduct && topDownProducts.length > 0;
  }

  @Selector([ProductsState.selectedProductId])
  static isBottomUpModeActive(selectedProductId?: number): boolean {
    return selectedProductId === BOTTOM_UP_PRODUCT_ID;
  }

  @Selector()
  static metricId(state: ProductsStateModel) {
    return state.metricId;
  }

  @Selector()
  static productsLoading(state: ProductsStateModel) {
    return state.productsLoading;
  }

  @Selector()
  static fetching(state: ProductsStateModel) {
    return state.fetching;
  }

  @Selector()
  static productTable(state: ProductsStateModel) {
    return state.products;
  }

  @Selector()
  static productById(state: ProductsStateModel) {
    return (productId: number | null) => state.products.find((product) => product.id === productId)
  }

  @Selector()
  static productsTree(state: ProductsStateModel) {
    return state.products.map(({ specialAnalyses, ...product }) => ({
      label: product.name,
      data: product,
      expanded: true,
      children: specialAnalyses.map((specialAnalysis) => ({
        label: specialAnalysis.name,
        data: specialAnalysis,
        expanded: true,
      }))
    })) as TreeNode[]
  }

  @Selector()
  static productSettings(state: ProductsStateModel) {
    return state.settings
  }

  @Selector([SettingsState.filterSettings])
  static productSettingByProductId(filterSettings: FilterSetting[]) {
    return (productId: number | null) => filterSettings.find((setting) => setting.productId === productId)
  }

  @Selector([ProductsState.selectedProduct, SettingsState.filterSettings])
  static selectedProductSetting(selectedProduct?: Product, filterSettings?: FilterSetting[]) {
    return filterSettings?.find((setting) => setting.productId === selectedProduct?.id)
  }

  static productMethodologyIds(productId: number) {
    return createSelector([ProductsState], (state: ProductsStateModel) => {
      return state.products.find((product) => product.id === productId)?.productMethodologies.map((productMethodology) => productMethodology!.methodologyId) ?? [] as number[];
    });
  }

  @Action(Products.Initialize)
  async initialize({ getState, dispatch, patchState }: StateContext<ProductsStateModel>) {
    const { analysisMode, selectedProductId } = getState()

    patchState({
      metrics: this._generateMetricOptions(analysisMode, selectedProductId)
    })

    // Resolve first,  menu is built from it as soon as a product is preselected.
    const metricId = this._getMetricIdFromLocation(getState().metrics)
      ?? this.store.selectSnapshot(StorageState.metricId)
      ?? DEFAULT_METRIC_ID;

    patchState({
      productsLoading: true,
      metricId
    });
    dispatch(new Storage.SetSelectedMetricId(metricId));

    return dispatch(new Products.LoadProducts()).pipe(
      tap(() => {
        patchState({
          productsLoading: false
        });
      }),
      mergeMap(() => dispatch(new Products.PreSelectProductByIdOnLoad()))
    );
  }

  _generateMetricOptions(analysisMode: AnalysisMode, productId: number | undefined) {
    const rawMetricOptions = getMetricOptions(analysisMode, productId);
    return rawMetricOptions.filter(m => m.visible) as MetricOption[]
  }

  /**
   * Reads the metric off a dashboard deep link. Without it the menu would be built for the
   * last used metric and the DashboardIdGuard would reject the deep-linked dashboard.
   */
  _getMetricIdFromLocation(metricOptions: MetricOption[]): Metric | undefined {
    const deepLinkedDashboardId = new RegExp(`^/dashboards/(\\d+)/\\d+x(\\d+)x\\d+`).exec(window.location.pathname);
    if (!deepLinkedDashboardId) return undefined;

    const metricId = parseInt(deepLinkedDashboardId[2]);
    return metricOptions.some(option => option.id === metricId) ? metricId : undefined;
  }

  @Action(Products.PreSelectProductByIdOnLoad)
  preSelectProductByIdOnLoad({ dispatch, getState }: StateContext<ProductsStateModel>) {
    // Return early when no accessible products are found
    return this.store.select(ProductsState.accessibleProducts).pipe(
      filter(accessibleProducts => accessibleProducts.length > 0), // Ignore emissions until accessibleProducts is not empty
      first(), // Take the first non-empty emission and then complete
      switchMap((accessibleProducts) => {
        // Get the options for preselecting the product
        const inMemorySelectedProductId = getState().selectedProductId;
        const inStorageSelectedProductId = this.store.selectSnapshot(StorageState.selectedProductId);
        const firstAccessibleProductId = accessibleProducts[0].id;

        // Preselect the product either from memory, storage, or the first accessible product
        const productIdToSelect = inMemorySelectedProductId ?? inStorageSelectedProductId ?? firstAccessibleProductId;
        return dispatch(new Products.SelectProductById(productIdToSelect))
      }),
      catchError((error) => {
        console.error('Error preselecting product:', error);
        return of(new Products.PreSelectProductByIdOnLoadFailed());
      })
    )
  }

  @Action(Products.PreSelectProductByIdOnLoadFailed)
  preSelectProductByIdOnLoadFailed(ctx: StateContext<ProductsStateModel>) {
    console.warn('[ProductsState] No accessible products found! - Preselecting product failed!');
  }

  _traverseMenuItemAndCheckClaims(menuItem: MenuItem) {
    if (menuItem.requiredClaims && menuItem.requiredClaims.length > 0 && !this.store.selectSnapshot(AuthState.hasClaims(menuItem.requiredClaims!))) return null;
    const tmp: MenuItem = { ...menuItem, items: [] };
    if (menuItem.items) {
      for (const childItem in menuItem.items) {
        const tmpChildItem = this._traverseMenuItemAndCheckClaims(menuItem.items[childItem]);
        if (tmpChildItem) tmp.items!.push(tmpChildItem);
      }
    }
    if (tmp.items!.length === 0 && tmp.hideWhenNoItems) return null;
    if (tmp.items!.length === 0) tmp.items = undefined;
    return tmp;
  };

  _generateMenuModel(product: any, metricId: Metric) {
    if (!product) return [];
    return getProductMenuItems(product.id, metricId).map(item => this._traverseMenuItemAndCheckClaims(item as MenuItem)).filter(item => item !== null) as MenuItem[];
  }

  /** Checks whether a dashboard is reachable through the given menu, i.e. linked by a visible/disabled item. */
  _menuModelContainsRouterLink(menuItems: MenuItem[], routerLink: string): boolean {
    return menuItems.some((item) => {
      if (item.visible === false || item.disabled) return false;
      const itemRouterLink = Array.isArray(item.routerLink) ? item.routerLink.join('/') : item.routerLink;
      if (itemRouterLink === routerLink) return true;
      return item.items ? this._menuModelContainsRouterLink(item.items, routerLink) : false;
    });
  }

  @Action(Products.SelectProductById)
  selectProductById({
    patchState,
    getState,
    dispatch
  }: StateContext<ProductsStateModel>, { productId }: Products.SelectProductById) {
    const products = getState().products;

    // When Products not loaded yet, only set id and recall this method after products are loaded
    if (products === undefined || !products.length) {
      patchState({
        selectedProductId: productId,
      });
      return;
    }

    return this.store.select(ProductsState.accessibleProducts).pipe(
      filter(accessibleProducts => accessibleProducts.length > 0), // Ignore emissions until accessibleProducts is not empty
      first(), // Take the first non-empty emission and then complete
      map((accessibleProducts) => {
        const selectedProduct = accessibleProducts.find(product => product.id === productId);
        const isAccessible = selectedProduct !== undefined;
        if (!isAccessible) {
          return dispatch(new Products.UnauthorizedProductSelectionAttempt(productId));
        }

        // Bailed out when the selected product is already selected
        const currentState = getState();
        const isAlreadySelected = currentState.selectedProductId === productId && currentState.selectedProduct == selectedProduct
        if (isAlreadySelected) return;

        patchState({
          selectedProductId: productId,
          selectedProduct,
          selectedProductMenuModel: this._generateMenuModel(selectedProduct, currentState.metricId),
          metrics: this._generateMetricOptions(currentState.analysisMode, productId) as MetricOption[]
        });

        dispatch(Menus.LoadValidDashboardPaths);

        return dispatch(new Storage.SetSelectedProductId(productId));
      })
    )
  }

  @Action(Products.SelectAnalysisMode)
  selectAnalysisMode({ dispatch, patchState }: StateContext<ProductsStateModel>, { mode }: Products.SelectAnalysisMode) {
    patchState({
      analysisMode: mode
    })

    if (mode === AnalysisMode.BottomUp) {
      return dispatch(new Products.SelectProductById(BOTTOM_UP_PRODUCT_ID));
    }

    // Top-down has no dedicated product, so fall back to the first accessible one.
    const [firstTopDownProduct] = this.store.selectSnapshot(ProductsState.topDownProducts);
    if (!firstTopDownProduct) {
      console.warn('[ProductsState] No accessible top-down product found! - Switching analysis mode failed!');
      return;
    }
    return dispatch(new Products.SelectProductById(firstTopDownProduct.id));
  }

  @Action(Products.SelectMetric)
  selectMetric({ patchState, getState, dispatch }: StateContext<ProductsStateModel>, { metricId: incomingMetricId }: Products.SelectMetric) {
    const { metricId: selectedMetricId, selectedProduct } = getState();
    if (selectedMetricId === incomingMetricId) return;

    patchState({ metricId: incomingMetricId });
    dispatch(new Storage.SetSelectedMetricId(incomingMetricId));

    const selectedProductMenuModel = this._generateMenuModel(selectedProduct, incomingMetricId);
    patchState({ selectedProductMenuModel });
    dispatch(Menus.LoadValidDashboardPaths);

    this._followMetricChangeOnCurrentDashboard(incomingMetricId, selectedProductMenuModel);
  }

  /**
   * Dashboard ids encode the metric, so the dashboard the user is looking at disappears from the menu
   * when the metric changes. Keep them on the same analysis by switching to its equivalent instead.
   * Applies to any product (`/dashboards/{productId}/{analysis}x{metric}x{dimension}`), including
   * standortdossier/forecast suffixes.
   */
  _followMetricChangeOnCurrentDashboard(metricId: Metric, menuModel: MenuItem[]) {
    const path = decodeURI(this.router.url.split('?')[0]);
    const currentDashboard = /^\/dashboards\/(\d+)\/(\d+(?:x\d+){2,3})(.*)$/.exec(path);
    if (!currentDashboard) return;

    const [, productId, dashboardId, rest] = currentDashboard;
    const equivalentDashboardId = DashboardUtils.encodeDashboardId({
      ...DashboardUtils.decodeDashboardId(dashboardId),
      metricId,
    });
    const routerLink = `/dashboards/${productId}/${equivalentDashboardId}${rest}`;
    if (!this._menuModelContainsRouterLink(menuModel, routerLink)) return;

    this.router.navigateByUrl(routerLink);
  }

  @Action(Products.UnauthorizedProductSelectionAttempt)
  handleUnauthorizedProductSelectionAttempt({ dispatch }: StateContext<ProductsStateModel>, { productId }: Products.UnauthorizedProductSelectionAttempt) {
    console.warn(`[ProductsState] Unauthorized product ${productId} selection attempt.`);
    const accessibleProducts = this.store.selectSnapshot(ProductsState.accessibleProducts)
    dispatch(new Products.SelectProductById(accessibleProducts[0].id));
  }

  // Used for debugging purpose only
  @Action(Products.ClearSelectedProduct)
  clearSelectedProduct({ patchState, getState, dispatch }: StateContext<ProductsStateModel>) {
    dispatch(new Storage.ClearSelectedProductId());
    patchState({
      selectedProductId: undefined,
      selectedProduct: undefined,
      selectedProductMenuModel: this._generateMenuModel(undefined, getState().metricId)
    });
  }

  @Action(Products.LoadProducts)
  loadProducts({ patchState, getState, dispatch }: StateContext<ProductsStateModel>) {
    // if (getState().fetching) return; ToDo: Implement Error handling
    // patchState({ fetching: true })
    this.asyncInitializationService.updateTaskState('products', AsyncInitializationTaskState.PENDING);
    return this.productService.getProducts().pipe(
      tap((products) => {
            // sort products
            products = [...products];
            products.sort((a, b) => a.id > b.id ? 1 : -1);
        patchState({
          fetching: false,
          products
        });
            this.asyncInitializationService.updateTaskState('products', AsyncInitializationTaskState.SUCCESS);
        if (getState().selectedProductId !== undefined) {
          dispatch(new Products.SelectProductById(getState().selectedProductId!));
        }
      }
      ));
  }

  @Action(Products.CreateProduct)
  createProduct(ctx: StateContext<ProductsStateModel>, action: Products.CreateProduct) {
    return this.productService.createProduct(action.createProductInput).pipe(
      tap((newProduct) => {
        const state = ctx.getState()

        ctx.patchState({
          products: [...state.products!, newProduct]
        })

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Product created successfully.'
        })
      })
    )
  }

  @Action(Products.UpdateProduct)
  updateProduct(ctx: StateContext<ProductsStateModel>, action: Products.UpdateProduct) {
    return this.productService.updateProduct(action.updateProductInput).pipe(
      tap((updatedProduct) => {
        const state = ctx.getState()

        ctx.patchState({
          products: state.products!.map((product) => product.id === updatedProduct.id ? updatedProduct : product)
        })

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Product updated successfully.'
        })
      })
    )
  }

  @Action(Products.ToggleActive)
  toggleActive(ctx: StateContext<ProductsStateModel>, action: Products.ToggleActive) {
    const product = ctx.getState().products.find((product) => product.id === Number(action.id));

    const payload: UpdateProductInput = {
      id: product!.id,
      isEnabled: !product!.isEnabled
    }

    return this.productService.updateProduct(payload).pipe(
      tap((updatedProduct) => {
        const state = ctx.getState()

        ctx.patchState({
          products: state.products!.map((product) => product.id === updatedProduct.id ? updatedProduct : product)
        })

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${product?.name} is now ${payload.isEnabled ? 'enabled' : 'disabled'}.`
        })
      })
    )
  }

  @Action(Products.RemoveProduct)
  removeProduct(ctx: StateContext<ProductsStateModel>, action: Products.RemoveProduct) {
    return this.productService.removeProduct(action.productId).pipe(
      tap({
        next: (removedProduct) => {
          const state = ctx.getState()

          ctx.patchState({
            products: state.products!.filter((product) => product.id !== removedProduct.id)
          })

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Product deleted successfully.'
          });

          this.router.navigateByUrl('/administration/products')
        },
        error: (err) => {
          console.error(err)
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Delete product failed.'
          })
        }
      })
    )
  }
}
